import Message from 'shared_components/message';
import { axiosLabAPI } from 'services/axios';
import { API_GET_METHOD } from 'global_config/constant-global';
import { message as antdMessage } from 'antd';

const STORAGE_KEY = 'lab_messages';

// Fetch messages from API and save to localStorage
export async function fetchMessages() {
    let messages = null;

    try {
        const content = {
            method: API_GET_METHOD,
            url: 'settings/message'
        };

        const response = await axiosLabAPI(content);
        const { data } = await response;

        // Handle new API response format
        if (data && data.STATUSCODE === 200 && data.RECORDS) {
            messages = {};
            data.RECORDS.forEach(msg => {
                messages[msg.messageCode] = {
                    text: msg.messageDisplay,
                    type: msg.messageType
                };
            });

            localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
        }
    }
    catch(e) {
        Message.error();
    }

    return messages;
}

// Show message function - usage: showMessage('M0001') or showMessage(error) or showMessage(404)
export function showMessage(codeOrStatusOrError, config = {}) {
    // Check if it's an error object from axios
    if (codeOrStatusOrError && typeof codeOrStatusOrError === 'object' && codeOrStatusOrError.response) {
        const errorData = codeOrStatusOrError.response.data || {};
        const { MESSAGECODE, MESSAGE, MESSAGETYPE } = errorData;

        // If MESSAGECODE exists, check storage first
        if (MESSAGECODE) {
            let msg = null;
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                const messages = stored ? JSON.parse(stored) : {};
                msg = messages[MESSAGECODE];
            } catch (e) {
                // If storage fails, msg stays null
            }

            // If message exists in storage, use it
            if (msg) {
                let messageText = msg.text;

                // Replace params if provided
                if (config.params) {
                    Object.keys(config.params).forEach(key => {
                        messageText = messageText.replace(`{${key}}`, config.params[key]);
                    });
                }

                const options = {
                    content: messageText,
                    duration: config.duration || 3,
                    onClose: config.onClose
                };

                // Use type from storage
                if (msg.type === 'MESSAGE_SUCCESS') {
                    return antdMessage.success(options);
                } else if (msg.type === 'MESSAGE_ERROR') {
                    return antdMessage.error(options);
                } else if (msg.type === 'MESSAGE_WARNING') {
                    return antdMessage.warning(options);
                } else {
                    return antdMessage.info(options);
                }
            }

            // If no message in storage, display code with MESSAGETYPE
            const options = {
                content: MESSAGECODE,
                duration: config.duration || 3,
                onClose: config.onClose
            };

            if (MESSAGETYPE === 'MESSAGE_SUCCESS') {
                return antdMessage.success(options);
            } else if (MESSAGETYPE === 'MESSAGE_ERROR') {
                return antdMessage.error(options);
            } else if (MESSAGETYPE === 'MESSAGE_WARNING') {
                return antdMessage.warning(options);
            } else {
                return antdMessage.info(options);
            }
        }

        // If no MESSAGECODE, automatic ERROR and display MESSAGE
        if (MESSAGE) {
            return antdMessage.error({
                content: MESSAGE,
                duration: config.duration || 3,
                onClose: config.onClose
            });
        }

        // Fallback to generic error
        return antdMessage.error({
            content: 'An unexpected error occurred',
            duration: config.duration || 3,
            onClose: config.onClose
        });
    }

    // Check if it's an Error object without response property
    if (codeOrStatusOrError instanceof Error) {
        return antdMessage.error({
            content: codeOrStatusOrError.message || 'An error occurred',
            duration: config.duration || 3,
            onClose: config.onClose
        });
    }

    // If it's a message code string or number
    let messageCode;
    if (typeof codeOrStatusOrError === 'number') {
        // If it's a number (HTTP status), map to message code
        messageCode = getMessageCodeByStatus(codeOrStatusOrError);
    } else if (typeof codeOrStatusOrError === 'string') {
        messageCode = codeOrStatusOrError;
    } else {
        // Fallback for unknown types
        return antdMessage.error({
            content: 'An error occurred',
            duration: config.duration || 3,
            onClose: config.onClose
        });
    }

    // Try to get message from localStorage
    let msg = null;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const messages = stored ? JSON.parse(stored) : {};
        msg = messages[messageCode];
    } catch (error) {
        // If localStorage fails, msg stays null
    }

    // If message exists in storage
    if (msg) {
        let messageText = msg.text;

        // Replace params if provided
        if (config.params) {
            Object.keys(config.params).forEach(key => {
                messageText = messageText.replace(`{${key}}`, config.params[key]);
            });
        }

        const options = {
            content: messageText,
            duration: config.duration || 3,
            onClose: config.onClose
        };

        // Display based on message type from storage
        if (msg.type === 'MESSAGE_SUCCESS') {
            return antdMessage.success(options);
        } else if (msg.type === 'MESSAGE_ERROR') {
            return antdMessage.error(options);
        } else if (msg.type === 'MESSAGE_WARNING') {
            return antdMessage.warning(options);
        } else {
            return antdMessage.info(options);
        }
    }

    // If no message in storage, just display the code as success
    return antdMessage.success({
        content: messageCode,
        duration: config.duration || 3,
        onClose: config.onClose
    });
}

// Deprecated: Use showMessage(error) instead
function getMessageCodeByStatus(status) {
    const statusMap = {
        404: 'M0001',
        400: 'M0005',
        401: 'M0004',
        403: 'M0004',
        500: 'M0004',
        502: 'M0004',
        503: 'M0004',
    };

    return statusMap[status] || 'M0004';
}